import type { ErrorCode, KnownErrorCode } from "@/services/aiIdentification";
import { KnownErrorCodes } from "@/services/aiIdentification";
import type { IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { faHexagonExclamation, faImageCircleXmark, faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { toValue, type MaybeRefOrGetter } from "vue";
import { useGettext } from "vue3-gettext";

function isKnownErrorCode(code: string): code is KnownErrorCode {
  return new Set<string>(Object.values(KnownErrorCodes)).has(code);
}

export function useAiError(code: MaybeRefOrGetter<ErrorCode>) {
  const { $gettext } = useGettext();

  const type2iconMap: Record<KnownErrorCode, IconDefinition> = {
    [KnownErrorCodes.VisionNoCardDetected]: faImageCircleXmark,
    [KnownErrorCodes.VisionMultipleCards]: faImageCircleXmark,
    [KnownErrorCodes.VisionNotPokemon]: faImageCircleXmark,
    [KnownErrorCodes.VisionNotCard]: faImageCircleXmark,
    [KnownErrorCodes.VisionCardCropped]: faImageCircleXmark,
    [KnownErrorCodes.VisionUnsupportedLanguage]: faMagnifyingGlass,
    [KnownErrorCodes.SearchNotFound]: faMagnifyingGlass,
    [KnownErrorCodes.SearchNoConfidentMatch]: faMagnifyingGlass,
    [KnownErrorCodes.VisionRateLimited]: faHexagonExclamation,
    [KnownErrorCodes.VisionTimeout]: faHexagonExclamation,
    [KnownErrorCodes.VisionApiError]: faHexagonExclamation,
    [KnownErrorCodes.SearchRateLimited]: faHexagonExclamation,
    [KnownErrorCodes.SearchTimeout]: faHexagonExclamation,
    [KnownErrorCodes.SearchApiError]: faHexagonExclamation,
    [KnownErrorCodes.DescRateLimited]: faHexagonExclamation,
    [KnownErrorCodes.DescTimeout]: faHexagonExclamation,
    [KnownErrorCodes.DescApiError]: faHexagonExclamation,
    [KnownErrorCodes.PipelineTimeout]: faHexagonExclamation,
  };

  const type2nameMap: Record<KnownErrorCode, string> = {
    [KnownErrorCodes.VisionNoCardDetected]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.VisionMultipleCards]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.VisionNotPokemon]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.VisionNotCard]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.VisionCardCropped]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.VisionUnsupportedLanguage]: $gettext("Karta nenalezena"),
    [KnownErrorCodes.SearchNotFound]: $gettext("Karta nenalezena"),
    [KnownErrorCodes.SearchNoConfidentMatch]: $gettext("Karta nenalezena"),
    [KnownErrorCodes.VisionRateLimited]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.VisionTimeout]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.VisionApiError]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.SearchRateLimited]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.SearchTimeout]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.SearchApiError]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.DescRateLimited]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.DescTimeout]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.DescApiError]: $gettext("Chyba v poskytnuté fotografii"),
    [KnownErrorCodes.PipelineTimeout]: $gettext("Chyba v poskytnuté fotografii"),
  };

  const icon = computed((): IconDefinition => {
    const c = toValue(code);
    if (isKnownErrorCode(c)) {
      return type2iconMap[c];
    } else {
      return faHexagonExclamation;
    }
  });

  const name = computed((): string => {
    const c = toValue(code);
    if (isKnownErrorCode(c)) {
      return type2nameMap[c];
    } else {
      return $gettext("Chyba v poskytnuté fotografii");
    }
  });

  return {
    icon,
    name,
  };
}
